/**
 * Shopify owns persistence and the banner. This bridge only distributes its
 * effective permissions to GTM and theme consumers; it never records a choice.
 * The legacy cleanup must execute before Shopify initializes.
 */
(function(window, document) {
  'use strict';
  if (window.HappyDayConsent) return;

  /**
   * @typedef {Object} ConsentState
   * @property {number} schemaVersion
   * @property {boolean} ready
   * @property {boolean} analyticsAllowed
   * @property {boolean} marketingAllowed
   * @property {boolean} preferencesAllowed
   * @property {boolean} saleOfDataAllowed
   * @property {{analytics:string,marketing:string,preferences:string,sale_of_data:string}} choices
   */
  /** @type {ConsentState} */
  var state;
  var revision = 0;
  var featureRequested = false;
  var attempts = 0;
  var maxAttempts = 40;
  var timer = null;
  var warned = {};

  function warn(code, message) {
    if (warned[code]) return;
    warned[code] = true;
    console.warn('[Consent] ' + message);
  }

  function migrationBlocked() {
    return !!(window.HappyDayConsentMigration && window.HappyDayConsentMigration.blocked);
  }

  function privacy() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  function choices(api) {
    var raw = api && typeof api.currentVisitorConsent === 'function'
      ? api.currentVisitorConsent() : {};
    var result = {};
    ['analytics', 'marketing', 'preferences', 'sale_of_data'].forEach(function(key) {
      var value = raw && raw[key];
      result[key] = value === 'yes' || value === 'no' ? value : '';
    });
    return Object.freeze(result);
  }

  function denied() {
    return {
      schemaVersion: 1, ready: false,
      analyticsAllowed: false, marketingAllowed: false,
      preferencesAllowed: false, saleOfDataAllowed: false,
      choices: choices(null)
    };
  }

  /**
   * Publish the snapshot before notifying consumers, avoiding dependency on the
   * order of independent visitorConsentCollected listeners.
   * @param {ConsentState} next
   */
  function publish(next) {
    if (state && JSON.stringify(next) === JSON.stringify(state)) return;
    state = Object.freeze(next);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'consent_ready', hd_consent: state });
    // Existing product/list/search trackers wait for resolution, not a grant.
    if (state.ready && !window.__gtmConsentReady) {
      window.__gtmConsentReady = true;
      document.dispatchEvent(new window.CustomEvent('gtm:consent-ready', { detail: state }));
    }
    document.dispatchEvent(new window.CustomEvent('hd:consent-changed', { detail: state }));
  }

  function apiReady(api) {
    return api && ['analyticsProcessingAllowed', 'marketingAllowed', 'preferencesProcessingAllowed',
      'saleOfDataAllowed'].every(function(method) { return typeof api[method] === 'function'; });
  }

  function readApi() {
    var api = privacy();
    if (migrationBlocked() || !apiReady(api)) return false;
    try {
      publish({
        schemaVersion: 1, ready: true,
        analyticsAllowed: api.analyticsProcessingAllowed() === true,
        marketingAllowed: api.marketingAllowed() === true,
        preferencesAllowed: api.preferencesProcessingAllowed() === true,
        saleOfDataAllowed: api.saleOfDataAllowed() === true,
        choices: choices(api)
      });
      return true;
    } catch (error) {
      publish(denied());
      warn('read', 'Could not read Shopify permissions; optional tracking remains denied.');
      return false;
    }
  }

  window.HappyDayConsent = Object.freeze({
    getState: function() { return state; }
  });
  // GTM already starts denied. An "update" here would end its wait_for_update
  // interval before Shopify restores a returning visitor's saved permissions.
  state = Object.freeze(denied());

  document.addEventListener('visitorConsentCollected', function(event) {
    revision++;
    if (timer !== null) window.clearTimeout(timer);
    if (migrationBlocked()) {
      publish(denied());
      return;
    }
    var detail = event && event.detail;
    var fields = ['analyticsAllowed', 'marketingAllowed', 'preferencesAllowed', 'saleOfDataAllowed'];
    if (!detail || !fields.every(function(key) { return typeof detail[key] === 'boolean'; })) {
      publish(denied());
      warn('event', 'Invalid Shopify consent event; optional tracking remains denied.');
      return;
    }
    try {
      publish({
        schemaVersion: 1, ready: true,
        analyticsAllowed: detail.analyticsAllowed,
        marketingAllowed: detail.marketingAllowed,
        preferencesAllowed: detail.preferencesAllowed,
        saleOfDataAllowed: detail.saleOfDataAllowed,
        choices: choices(privacy())
      });
    } catch (error) {
      publish(denied());
      warn('event-read', 'Could not read changed consent; optional tracking remains denied.');
    }
  });

  function initialize() {
    // A real visitor event wins over any delayed initialization callback.
    if (revision > 0 || migrationBlocked() || readApi()) return;
    attempts++;
    var shopify = window.Shopify;
    if (!featureRequested && shopify && typeof shopify.loadFeatures === 'function') {
      featureRequested = true;
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], function(error) {
          if (revision > 0) return;
          if (error) {
            warn('load', 'Shopify privacy API failed to load; optional tracking remains denied.');
            return;
          }
          if (readApi() && timer !== null) window.clearTimeout(timer);
        });
      } catch (error) {
        warn('load', 'Shopify privacy API failed to load; optional tracking remains denied.');
      }
    }
    if (state.ready) return;
    if (attempts < maxAttempts) {
      timer = window.setTimeout(initialize, 250);
    } else {
      warn('timeout', 'Shopify privacy API was unavailable; optional tracking remains denied.');
    }
  }
  initialize();
})(window, document);
